import * as THREE from 'three';
import type {SketchEllipseDefinition} from '../../mesh/sketch/SketchEllipseObject';
import type {ReferencePlane} from '../../reference/ReferencePlane';

/**
 * 草图椭圆参数几何服务。
 *
 * 职责：统一完整椭圆与部分椭圆的参数角、真实曲线点、标准方程坐标和点参数映射。
 * 捕捉、裁剪求交和裁剪实体重建必须依赖本服务，禁止各自使用显示采样弦或复制参数公式。
 */
export class SketchEllipseParametricGeometryService {
    /** 单圈弧度。 */
    public static readonly FULL_TURN = Math.PI * 2;

    /** 完整椭圆扫掠判定容差。 */
    private static readonly FULL_SWEEP_TOLERANCE = 1e-12;

    /** 复用的中心局部坐标，避免捕捉热路径反复分配。 */
    private readonly centerLocal = new THREE.Vector2();

    /** 复用的目标点局部坐标，避免参数投影反复分配。 */
    private readonly pointLocal = new THREE.Vector2();

    /** 复用的标准方程坐标。 */
    private readonly canonicalPoint = new THREE.Vector2();

    /**
     * 判断定义是否表示完整椭圆。
     * @param definition 椭圆定义。
     * @returns 扫掠覆盖完整一圈时返回 true。
     */
    public isFullEllipse(definition: Required<SketchEllipseDefinition>): boolean {
        return Math.abs(
            Math.abs(this.resolveSignedSweep(definition)) - SketchEllipseParametricGeometryService.FULL_TURN,
        ) <= SketchEllipseParametricGeometryService.FULL_SWEEP_TOLERANCE;
    }

    /**
     * 解析与椭圆定义一致的有向扫掠角。
     *
     * 说明：正值表示逆时针，负值表示顺时针；完整椭圆保留整圈而不是归零。
     *
     * @param definition 椭圆定义。
     * @returns 有向扫掠角。
     */
    public resolveSignedSweep(definition: Required<SketchEllipseDefinition>): number {
        const fullTurn = SketchEllipseParametricGeometryService.FULL_TURN;
        const rawDifference = definition.endAngle - definition.startAngle;
        if (
            Math.abs(rawDifference) <= SketchEllipseParametricGeometryService.FULL_SWEEP_TOLERANCE
            || Math.abs(rawDifference) >= fullTurn - SketchEllipseParametricGeometryService.FULL_SWEEP_TOLERANCE
        ) {
            return definition.clockwise ? -fullTurn : fullTurn;
        }

        if (definition.clockwise) {
            return -this.normalizePositiveAngle(definition.startAngle - definition.endAngle);
        }
        return this.normalizePositiveAngle(definition.endAngle - definition.startAngle);
    }

    /**
     * 按归一化参数解析椭圆参数角。
     * @param definition 椭圆定义。
     * @param parameterT 归一化参数。
     * @returns 椭圆参数角。
     */
    public resolveAngleAtParameter(
        definition: Required<SketchEllipseDefinition>,
        parameterT: number,
    ): number {
        return definition.startAngle + this.resolveSignedSweep(definition) * parameterT;
    }

    /**
     * 计算椭圆参数角对应的真实世界点。
     *
     * @param referencePlane 椭圆所属参考面。
     * @param definition 椭圆定义。
     * @param angle 椭圆参数角。
     * @param target 接收结果的复用向量。
     * @returns 真实椭圆世界点。
     */
    public evaluateWorldPointAtAngle(
        referencePlane: ReferencePlane,
        definition: Required<SketchEllipseDefinition>,
        angle: number,
        target: THREE.Vector3 = new THREE.Vector3(),
    ): THREE.Vector3 {
        // 椭圆定义的圆心是世界坐标，参数方程必须先在参考面二维坐标系中求值。
        referencePlane.worldToLocalTo(definition.centerWorld, this.centerLocal);
        const cosineAngle = Math.cos(angle);
        const sineAngle = Math.sin(angle);
        const cosineRotation = Math.cos(definition.rotation);
        const sineRotation = Math.sin(definition.rotation);
        const localX = this.centerLocal.x
            + definition.radiusX * cosineAngle * cosineRotation
            - definition.radiusY * sineAngle * sineRotation;
        const localY = this.centerLocal.y
            + definition.radiusX * cosineAngle * sineRotation
            + definition.radiusY * sineAngle * cosineRotation;
        return referencePlane.localToWorldTo(localX, localY, target);
    }

    /**
     * 计算归一化参数对应的真实椭圆世界点。
     * @param referencePlane 椭圆所属参考面。
     * @param definition 椭圆定义。
     * @param parameterT 归一化参数。
     * @param target 接收结果的复用向量。
     * @returns 真实椭圆世界点。
     */
    public evaluateWorldPointAtParameter(
        referencePlane: ReferencePlane,
        definition: Required<SketchEllipseDefinition>,
        parameterT: number,
        target: THREE.Vector3 = new THREE.Vector3(),
    ): THREE.Vector3 {
        return this.evaluateWorldPointAtAngle(
            referencePlane,
            definition,
            this.resolveAngleAtParameter(definition, parameterT),
            target,
        );
    }

    /**
     * 将世界点转换到椭圆标准方程坐标系。
     *
     * 标准坐标满足真实椭圆方程 `x² + y² = 1`，可直接用于直线—椭圆二次方程求交。
     *
     * @param referencePlane 椭圆所属参考面。
     * @param definition 椭圆定义。
     * @param worldPoint 世界点。
     * @param target 接收结果的复用二维向量。
     * @returns 椭圆标准方程坐标。
     */
    public transformWorldPointToCanonical(
        referencePlane: ReferencePlane,
        definition: Required<SketchEllipseDefinition>,
        worldPoint: THREE.Vector3,
        target: THREE.Vector2 = new THREE.Vector2(),
    ): THREE.Vector2 {
        referencePlane.worldToLocalTo(definition.centerWorld, this.centerLocal);
        referencePlane.worldToLocalTo(worldPoint, this.pointLocal);
        const translatedX = this.pointLocal.x - this.centerLocal.x;
        const translatedY = this.pointLocal.y - this.centerLocal.y;
        const cosineRotation = Math.cos(definition.rotation);
        const sineRotation = Math.sin(definition.rotation);
        const rotatedX = cosineRotation * translatedX + sineRotation * translatedY;
        const rotatedY = -sineRotation * translatedX + cosineRotation * translatedY;
        return target.set(
            rotatedX / definition.radiusX,
            rotatedY / definition.radiusY,
        );
    }

    /**
     * 将真实椭圆上的世界点映射为当前椭圆定义的归一化参数。
     *
     * @param referencePlane 椭圆所属参考面。
     * @param definition 椭圆定义。
     * @param worldPoint 真实椭圆世界点。
     * @param angleTolerance 部分椭圆参数域判定容差。
     * @returns 有效参数；点不在部分椭圆参数域或定义退化时返回 null。
     */
    public projectWorldPointToParameter(
        referencePlane: ReferencePlane,
        definition: Required<SketchEllipseDefinition>,
        worldPoint: THREE.Vector3,
        angleTolerance: number,
    ): number | null {
        if (definition.radiusX <= 0 || definition.radiusY <= 0) {
            return null;
        }

        this.transformWorldPointToCanonical(referencePlane, definition, worldPoint, this.canonicalPoint);
        const angle = this.normalizePositiveAngle(Math.atan2(this.canonicalPoint.y, this.canonicalPoint.x));
        const signedSweep = this.resolveSignedSweep(definition);
        const sweep = Math.abs(signedSweep);
        if (sweep <= angleTolerance) {
            return null;
        }

        const offset = signedSweep < 0
            ? this.normalizePositiveAngle(definition.startAngle - angle)
            : this.normalizePositiveAngle(angle - definition.startAngle);
        if (!this.isFullEllipse(definition) && offset > sweep + angleTolerance) {
            return null;
        }

        // 端点求交可能因浮点误差得到极小越界值，参数域内统一夹取到闭区间。
        return THREE.MathUtils.clamp(offset / sweep, 0, 1);
    }

    /**
     * 将角度归一化到 `[0, 2π)`。
     * @param angle 原始角度。
     * @returns 正向归一化角度。
     */
    private normalizePositiveAngle(angle: number): number {
        const fullTurn = SketchEllipseParametricGeometryService.FULL_TURN;
        const normalized = ((angle % fullTurn) + fullTurn) % fullTurn;
        return normalized >= fullTurn - SketchEllipseParametricGeometryService.FULL_SWEEP_TOLERANCE
            ? 0
            : normalized;
    }
}
